import { Box, Typography } from '@material-ui/core'
import { darken } from 'polished'
import { PropsWithChildren, ReactNode, useState } from 'react'
import styled from 'styled-components'

export interface IFancySwitchProps {
  /** The label that shows the current state of switch */
  currentStateLabel?: (isOn: boolean) => ReactNode
  /** The label for the right sided value on switch */
  onDisabledState?: () => ReactNode
  /** The label for the left sided value on switch */
  offDisabledState?: () => ReactNode
  /** The width of the switch */
  width?: string
  /** The height of the switch */
  height?: string
  /** Function run when switch is clicked */
  onChange?: (isOn: boolean, evt: any) => void
  /** The current state of  */
  state?: boolean
  /** When small is false, we'll use Large styling as described in design system */
  small?: boolean
  /** Main switch border radius */
  outerBorderRadius?: string
  /** Inner pill border radius */
  pillBorderRadius?: string
  // Colours
  /** Background colour */
  bgcolor?: string
  /** Active state pill background colour */
  activeBGColor?: string
  /** Active pill text colour */
  onActiveTextColor?: string
  /** When the on text isn't the selected option, this sets the colour, the light grey you see in designs, for example */
  onInactiveTextColor?: string
  /** When the off text isn't the selected option, this sets the colour, the light grey you see in designs, for example */
  offInactiveTextColor?: string

  /**A boolean passed when using the switch on the applicants' details page to apply styles when the hire switch is active */
  hired?: boolean

  disabled?: boolean
}

const StyledSwitchWrapper = styled(Box)<{
  activeBGColor?: string
  onActiveTextColor?: string
  onInactiveTextColor?: string
  offInactiveTextColor?: string
  outerBorderRadius?: string
  pillBorderRadius?: string
  small?: boolean
  
}>`
  &&,
  && * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  && {
    background-color: ${props => props.bgcolor || props.theme.colors.bgPrimary};
    border-radius: ${props => (props.small ? '6px' : props.outerBorderRadius)};
    display: flex;
    justify-content: center;
    align-items: center;
    color: whitesmoke;
    position: relative;
    overflow: hidden;
    cursor: pointer;
  }

  && .switch-bg,
  && .switch-labels {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  && .switch-bg {
    z-index: 2;
  }

  && .switch-labels {
    z-index: 1;
  }

  && .switch-labels {
    display: flex;
    justify-content: center;
  }

  && .switch-bg-pill {
    width: 50%;
    height: 100%;
    padding: ${props => (props.small ? '1px' : '2px')};
    display: flex;
    transition: transform 0.25s;
  }

  &&.active .switch-bg-pill {
    transform: translateX(100%);
  }

  && .switch-bg-pill-inner {
    width: 100%;
    height: 100%;
    background-color: ${props => props.activeBGColor || props.theme.colors.supplementary};
    border-radius: ${props => (props.small ? '6px' : props.pillBorderRadius)};
    color: ${props => props.onActiveTextColor || props.theme.colors.accent1};
    display: flex;
    justify-content: center;
    align-items: center;
  }

  && .switch-bg-pill-hired {
    background-color: ${props => props.theme.colors.secondary};
  }

  && .switch-label-container {
    flex-shrink: 0;
    flex-grow: 1;
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${props => props.onInactiveTextColor || darken(0.7, props.theme.colors.accent1)};
    transition: color 0.25s;
  }

  &&.active .switch-label-container.inactive {
    color: ${props => props.offInactiveTextColor || darken(0.7, props.theme.colors.accent1)};
  }
`

export const FancySwitch = ({
  currentStateLabel,
  state,
  onChange,
  width = '150px',
  height = '40px',
  onDisabledState,
  offDisabledState,
  bgcolor,
  activeBGColor,
  onActiveTextColor,
  onInactiveTextColor,
  offInactiveTextColor,
  small = false,
  outerBorderRadius = '12px',
  pillBorderRadius = '8px',
  hired,
  disabled
}: PropsWithChildren<IFancySwitchProps>) => {
  return (
    <StyledSwitchWrapper
      style={{opacity: disabled ? '0.3' : '', cursor: disabled ? 'not-allowed' : ''}}
      width={small ? '76px' : width}
      height={small ? '27px' : height}
      onClick={evt => {
        const newState = !state

        onChange?.(newState, evt)
      }}
      className={['switch', state ? 'active' : ''].join(' ')}
      bgcolor={bgcolor}
      activeBGColor={activeBGColor}
      onActiveTextColor={onActiveTextColor}
      onInactiveTextColor={onInactiveTextColor}
      offInactiveTextColor={offInactiveTextColor}
      outerBorderRadius={outerBorderRadius}
      pillBorderRadius={pillBorderRadius}
      small={small}
    >
      <Box className="switch-bg">
        <Box className="switch-bg-pill">
          <Box className={`switch-bg-pill-inner ${hired ? 'switch-bg-pill-hired' : ''}`}>
            <Typography variant="overline">{currentStateLabel?.(state) || (state ? 'ON' : 'OFF')}</Typography>
          </Box>
        </Box>
      </Box>
      <Box className="switch-labels">
        <Box className="switch-label-container inactive">
          <Typography variant="overline">{offDisabledState?.() || 'OFF'}</Typography>
        </Box>
        <Box className="switch-label-container active">
          <Typography variant="overline">{onDisabledState?.() || 'ON'}</Typography>
        </Box>
      </Box>
    </StyledSwitchWrapper>
  )
}
